import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, BRAND_COLOR, baseChartOptions } from '../shared/echarts-config';
import { ChartContainer } from '../shared/ChartContainer';
import { EmptyState } from '../shared/EmptyState';
import type { TrendPoint } from '../../../types/analytics.types';

type EngagementTrendChartProps = {
  data: TrendPoint[];
};

const TYPE_LABELS: Record<string, string> = {
  CONSULTATION_REQUEST: 'Consultation Requests',
  CALENDLY_BOOKING: 'Calendly Bookings',
  INTAKE_FORM_OPEN: 'Intake Form Opens',
  FIRST_MESSAGE: 'First Messages',
  // Ticket 04: SP-initiated first messages get their own bucket instead of
  // being conflated with CS-initiated billable First Messages.
  SP_OUTBOUND_FIRST_MESSAGE: 'SP-Outbound First Messages',
  FA_PROVIDER_REFERRAL: 'FA Referrals',
  SP_PROVIDER_REFERRAL: 'SP Referrals',
  FA_FINANCIAL_ADVISOR_REFERRAL: 'FA to FA Referrals',
  SP_FINANCIAL_ADVISOR_REFERRAL: 'SP to FA Referrals',
  NETWORK_CONNECTION: 'Network Connections',
};

const TYPE_ORDER = Object.keys(TYPE_LABELS);

const buildChartOptions = (data: TrendPoint[]) => ({
  ...baseChartOptions,
  tooltip: {
    trigger: 'axis' as const,
    formatter: (params: Array<{ dataIndex: number }>) => {
      const idx = params[0]?.dataIndex;
      if (idx == null) return '';
      const point = data[idx];
      if (!point) return '';

      const d = new Date(point.date);
      const dateStr = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });

      let html = `<div style="font-weight:600;margin-bottom:6px">${dateStr}</div>`;
      html += `<div style="display:flex;justify-content:space-between;gap:16px">`;
      html += `<span>Total Engagements</span><span style="font-weight:600">${point.count}</span>`;
      html += `</div>`;

      if (point.byType && Object.keys(point.byType).length > 0) {
        html += `<div style="border-top:1px solid #e5e7eb;margin:6px 0"></div>`;
        for (const type of TYPE_ORDER) {
          const count = point.byType[type];
          if (count && count > 0) {
            const label = TYPE_LABELS[type] ?? type;
            html += `<div style="display:flex;justify-content:space-between;gap:16px;font-size:12px;color:#6b7280">`;
            html += `<span>${label}</span><span style="color:#374151">${count}</span>`;
            html += `</div>`;
          }
        }
      }

      return html;
    },
  },
  xAxis: {
    type: 'category' as const,
    data: data.map((p) => p.date),
    axisLabel: {
      formatter: (value: string) => {
        const d = new Date(value);
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      },
      fontSize: 12,
      color: '#6b7280',
    },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: [
    {
      name: 'Engagements',
      type: 'line',
      data: data.map((p) => p.count),
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, color: BRAND_COLOR },
      itemStyle: { color: BRAND_COLOR },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(61, 101, 96, 0.25)' },
            { offset: 1, color: 'rgba(61, 101, 96, 0.02)' },
          ],
        },
      },
    },
  ],
});

export const EngagementTrendChart = ({ data }: EngagementTrendChartProps) => {
  if (data.length === 0) {
    return (
      <ChartContainer
        title="Engagement Trend"
        subtitle="Daily engagement activity over the selected period"
      >
        <EmptyState message="No engagement trend data available for the selected date range." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer
      title="Engagement Trend"
      subtitle="Daily engagement activity over the selected period"
    >
      <ReactEChartsCore
        echarts={echarts}
        option={buildChartOptions(data)}
        style={{ height: 300 }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
